import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { FlexContainer, GeneralButton, VerticalSpace } from '@/components'
import * as types from '../../../store/types'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import api from '@bigfile/api'
import { FlowFileUploader } from '@appbase/components/common'
import { invalidateUrl } from '@appbase/util'
import { FlowFileMeta } from '@appbase/components/common/FlowFileUploader/flow-file-meta'
import { FlowFile } from '@appbase/libs/flowjs'

@Component({
	components: { FlowFileUploader, FlexContainer, GeneralButton, VerticalSpace },
	computed: {
		...mapGetters({
			files: types.FILE_LIST
		}),
	},
	methods: {
		...mapActions({
			toggleSelectAll: types.FILE_SELECT_ALL_TOGGLE,
		}),
		...mapMutations({
			deleteSelectedFiles: types.FILE_DELETE,
		}),
	},
})
export default class BigFileAttachment extends Vue {
    @Prop({ type: Object }) metaData!: FlowFileMeta | null;

	//
	// ────────────────────────────────────────────────────── I ──────────
	//   :::::: G E T T E R S : :  :   :    :     :        :          :
	// ────────────────────────────────────────────────────────────────
	//

	files!: FlowFile[]

	//
	// ────────────────────────────────────────────────────── I ──────────
	//   :::::: A C T I O N S : :  :   :    :     :        :          :
	// ────────────────────────────────────────────────────────────────
	//

	toggleSelectAll!: () => void

	//
	// ────────────────────────────────────────────────────────── I ──────────
	//   :::::: M U T A T I O N S : :  :   :    :     :        :          :
	// ────────────────────────────────────────────────────────────────────
	//

	deleteSelectedFiles!: () => void

	//
	// ──────────────────────────────────────────────── I ──────────
	//   :::::: D A T A : :  :   :    :     :        :          :
	// ──────────────────────────────────────────────────────────
	//

	uploadUrl: string = ''
	deleteUrl: string = ''
	meta: FlowFileMeta | null = null;
	loadState: boolean = false
	useLimit100: boolean = false
	//
	// ──────────────────────────────────────────────────────── I ──────────
	//   :::::: C O M P U T E D : :  :   :    :     :        :          :
	// ──────────────────────────────────────────────────────────────────
	//

	get disableButton() {
		return (
			this.files.length === 0 ||
			this.files.filter((x) => x.percent < 100).length > 0
		)
	}

	get officeLang() {
		return Office.context.displayLanguage;
	}

    get dragDescLine1() {
        const messages: { [key: string]: string } = {
            'ko-kr': '파일을 <b class="ft-blue">마우스</b>로 끌어다놓거나',
            'en-us': 'Drag and drop the file with <b class="ft-blue">mouse</b>',
            'zh-cn': '用<b class="ft-blue">鼠标</b>拖放文件',
            'ru-ru': 'Перетащите файл <b class="ft-blue">мышью</b>',
            'uz-uz': 'Faylni <b class="ft-blue">sichqoncha</b> bilan sudrab oling',
            'uz-latn-uz': 'Faylni <b class="ft-blue">sichqoncha</b> bilan sudrab oling', // 우즈벡어 추가 대비
        };
        return messages[this.officeLang.toLowerCase()] || 'Drag and drop the file with <b class="ft-blue">mouse</b>';
  }

    get dragDescLine2() {
        const messages: { [key: string]: string } = {
            'ko-kr': '<b class="ft-blue">파일 선택</b> 버튼을 이용해주세요',
            'en-us': 'or use the <b class="ft-blue">select file</b> button',
            'zh-cn': '或使用<b class="ft-blue">选择文件</b>按钮',
            'ru-ru': 'или используйте кнопку <b class="ft-blue">выбора файла</b>',
            'uz-uz': 'yoki <b class="ft-blue">fayl tanlash</b> tugmasidan foydalaning',
            'uz-latn-uz': 'yoki <b class="ft-blue">fayl tanlash</b> tugmasidan foydalaning', // 우즈벡어 추가 대비
        };
        return messages[this.officeLang.toLowerCase()] || 'or use the <b class="ft-blue">select file</b> button';
    }
	//
	// ────────────────────────────────────────────────────────── I ────────── 
	//   :::::: L I F E C Y C L E : :  :   :    :     :        :          :
	// ────────────────────────────────────────────────────────────────────
	//

	created() {	
		const email = Office.context.mailbox.userProfile.emailAddress
        let code = this.generateAttachmentCode(email);
		this.uploadUrl = invalidateUrl('/cm/file/store/api/flow')
		this.deleteUrl = invalidateUrl('/cm/file/store/api/delete')
		this.useLimit100 = true
		
		this.meta = this.metaData ?? {
			code: code,
			codeValues: ['OfficeAddin', 'File', email, code],
			mode: 'create'
		}				
	}

	//
	// ────────────────────────────────────────────────────── I ──────────
	//   :::::: M E T H O D S : :  :   :    :     :        :          :
	// ────────────────────────────────────────────────────────────────
	//

	generateAttachmentCode(id: string) {
		if (!id) throw new Error('ARGUMENT_NULL_EXCEPTION : id')
		const tag = 'OFFICE_ADDIN_BIGFILE'
		const uid = `UID${id.toUpperCase()}${Date.now()}`
		const code = `${tag}_${uid}`
		// console.log('generateAttachmentCode', code)
		return code
	}

	async onAddToBody(position: string) {
		if(this.meta == null) {
            throw new Error('meta data is null');
        }

		const template = await api.template.getTemplate(this.meta.code)
		if (!template) {
			throw new Error('Template is empty or null!!!')
		}

		// 다국어 치환
		const localizedTemplate = template
			.replace('대용량 첨부파일', this.$t('대용량_첨부파일').toString())
			.replace('다운로드기한', this.$t('다운로드기한').toString());
		// 이후 localizedTemplate 사용
		
		const item = Office.context.mailbox.item
		if (item) {
			const coercionType = Office.CoercionType.Html			

			// Insert data in item body
			item.body.getTypeAsync(((
				asyncResult: Office.AsyncResult<Office.CoercionType>
			) => {
				console.log('getTypeAsync', asyncResult.value)
				// check the format of the message body
				if (
					asyncResult.status != Office.AsyncResultStatus.Failed &&
					asyncResult.value == coercionType
				) {
					if (position === 'cusor') {
						// Insert data at the current cursor position
						item.body.setSelectedDataAsync(
							localizedTemplate,
							{
								coercionType: Office.CoercionType.Html,
								asyncContext: { var3: 1, var4: 2 },
							},
							function (asyncResult) { }
						)
					} else if (position === 'top') {
						// Insert data at the beginning of the item body
						item.body.prependAsync(
							localizedTemplate,
							{
								coercionType: Office.CoercionType.Html,
								asyncContext: { var3: 1, var4: 2 },
							},
							function (asyncResult) { }
						)
					}
				}
			}) as any)
		}		
	}

	onDeleteSelectedFile() {
		this.deleteSelectedFiles()
	}

	onToggleSelectAll() {
		this.toggleSelectAll()
	}
}
